import type { MaybeRef, Reactive } from "vue"
import type { ServerResponseV1 } from "@/http"
import type { FlaggedContent, FlaggedContentStatus, FlaggedContentType } from "@/models"
import { useQuery } from "@tanstack/vue-query"
import { ref, toValue } from "vue"
import { v1 } from "@/axios"

export interface IListFlaggedContentFilters {
  contentType?: FlaggedContentType
  status?: FlaggedContentStatus
  page?: number
  perPage?: number
}

export async function listFlaggedContent(eventVaultId: number, filters: Reactive<IListFlaggedContentFilters> = {}) {
  const query = new URLSearchParams()

  if (filters.contentType)
    query.append("contentType", filters.contentType)
  if (filters.status)
    query.append("status", filters.status)
  if (filters.page)
    query.append("PageNumber", filters.page.toString())
  if (filters.perPage)
    query.append("PageSize", filters.perPage.toString())

  const endpoint = `/${eventVaultId}/moderate/flagged-content`
  const response = await v1.get<ServerResponseV1<FlaggedContent[]>>(endpoint, { params: query })

  return {
    data: response.data.data ?? [],
    meta: response.data.meta,
  }
}

export const FLAGGED_CONTENT_QUERY_KEY = "listFlaggedContent"

export function useListFlaggedContent(eventVaultId?: MaybeRef<number | undefined>, filters: Reactive<IListFlaggedContentFilters> = {}) {
  const pageCount = ref(0)

  const query = useQuery({
    queryKey: [FLAGGED_CONTENT_QUERY_KEY, eventVaultId, filters],
    queryFn: async () => {
      const response = await listFlaggedContent(toValue(eventVaultId)!, filters)

      if (response.meta?.totalPages !== undefined) {
        pageCount.value = response.meta.totalPages
      }
      else if (response.meta?.totalCount !== undefined && response.meta?.pageSize) {
        pageCount.value = Math.ceil(response.meta.totalCount / response.meta.pageSize) || 1
      }
      else if (filters.perPage && response.meta?.totalCount !== undefined) {
        pageCount.value = Math.ceil(response.meta.totalCount / filters.perPage) || 1
      }
      else if (response.data.length > 0) {
        pageCount.value = 1
      }

      return response
    },
    enabled: !!toValue(eventVaultId),
  })

  return { ...query, pageCount }
}
